import { Button, Checkbox, PageHeader } from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { CLASSIFICATION_TYPES, type ClassificationType } from "../stores/SettingsStore"
import { useI18nStore, useSettingsStore } from "../stores/StoreContext"

export const SettingsPage = observer(() => {
  const i18nStore = useI18nStore()
  const settingsStore = useSettingsStore()

  const handleToggleClassification = (type: ClassificationType) => {
    settingsStore.toggleClassification(type)
  }

  const handleToggleTeam = (team: string) => {
    settingsStore.toggleTeam(team)
  }

  const handleSelectAllTeams = () => {
    settingsStore.selectAllTeams()
  }

  const handleDeselectAllTeams = () => {
    settingsStore.deselectAllTeams()
  }

  const allTeams = settingsStore.allAvailableTeams

  return (
    <div className="container mx-auto py-6 px-4">
      <PageHeader title={i18nStore.t("settingsTitle")} subtitle={i18nStore.t("settingsSubtitle")} />

      <div className="mt-8 max-w-3xl space-y-10">
        {/* Classification Section */}
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">{i18nStore.t("settingsClassificationTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {i18nStore.t("settingsClassificationDescription")}
            </p>
          </div>

          {/* Checkbox Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {CLASSIFICATION_TYPES.map((type) => (
              <div key={type} className="flex items-center space-x-3">
                <Checkbox
                  id={`classification-${type}`}
                  checked={settingsStore.isClassificationSelected(type)}
                  onCheckedChange={() => handleToggleClassification(type)}
                />
                <label
                  htmlFor={`classification-${type}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {i18nStore.t(
                    // biome-ignore lint/suspicious/noExplicitAny: Dynamic i18n key construction
                    `classification${type.charAt(0).toUpperCase()}${type.slice(1)}` as any,
                  ) || type}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Teams Section */}
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">{i18nStore.t("settingsTeamsTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {i18nStore.t("settingsTeamsDescription")}
            </p>
          </div>

          {/* Select/Deselect All Buttons */}
          {allTeams.length > 0 && (
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={handleSelectAllTeams}>
                {i18nStore.t("settingsSelectAll")}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeselectAllTeams}>
                {i18nStore.t("settingsDeselectAll")}
              </Button>
            </div>
          )}

          {/* Team Checkbox Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {allTeams.map((team) => (
              <div key={team} className="flex items-center space-x-3">
                <Checkbox
                  id={`team-${team}`}
                  checked={settingsStore.isTeamSelected(team)}
                  onCheckedChange={() => handleToggleTeam(team)}
                />
                <label
                  htmlFor={`team-${team}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {team}
                </label>
              </div>
            ))}
          </div>

          {allTeams.length === 0 && (
            <p className="text-sm text-muted-foreground italic">{i18nStore.t("settingsNoTeams")}</p>
          )}
        </div>
      </div>
    </div>
  )
})
